import type { ScheduledBook, Venue } from "@book-club/shared";
import { BookStatsSchema } from "@book-club/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import sessionsService from "@/features/sessions/services/sessionsService";
import type { SessionWithDetails } from "@/features/sessions/types";
import { isOldMonth } from "@/features/sessions/utils/cacheUtils";
import DURATIONS from "@/shared/constants/durations";
import { sessionsKeys } from "./sessionsQueryKeys";

/**
 * Fetches sessions for a given month with resolved book and venue data
 *
 * Caching strategy:
 * - Sessions query: 28-day stale for past months, 5-minute stale for current month
 * - Books: Single fetch populates both detail and stats caches (28-day stale)
 * - Venues: 28-day stale (rarely change)
 *
 * @param year - Full year (e.g., 2026)
 * @param month - Month number (1-12)
 */
export function useSessionsForMonth(year: number, month: number) {
    const queryClient = useQueryClient();
    const isOld = isOldMonth(year, month);

    const query = useQuery<SessionWithDetails[], Error>({
        gcTime: DURATIONS.TWENTY_EIGHT_DAYS,
        queryFn: async () => {
            // Fetch sessions
            const sessions = await sessionsService.getSessionsForMonth(year, month);

            if (sessions.length === 0) {
                return [];
            }

            // Fetch and cache static data (books and venues)
            const bookIds = [...new Set(sessions.map((s) => s.bookId))];
            const venueIds = [...new Set(sessions.map((s) => s.venueId))];

            const [books, venues] = await Promise.all([
                Promise.all(
                    bookIds.map(async (bookId) => {
                        return queryClient.ensureQueryData({
                            queryFn: async () => {
                                // Single fetch — full document
                                const book = await sessionsService.fetchScheduledBook(bookId);

                                if (book) {
                                    // Populate stats cache as a side effect
                                    queryClient.setQueryData(
                                        sessionsKeys.books.stats(bookId),
                                        BookStatsSchema.parse({
                                            id: book.id,
                                            ratingCount: book.ratingCount,
                                            ratingSum: book.ratingSum,
                                            totalAttendees: book.totalAttendees,
                                            totalBookings: book.totalBookings,
                                        })
                                    );
                                }

                                return book;
                            },
                            queryKey: sessionsKeys.books.detail(bookId),
                            staleTime: DURATIONS.TWENTY_EIGHT_DAYS,
                        });
                    })
                ),
                Promise.all(
                    venueIds.map(async (venueId) => {
                        return queryClient.ensureQueryData({
                            queryFn: () => sessionsService.fetchVenue(venueId),
                            queryKey: sessionsKeys.venues.detail(venueId),
                            staleTime: DURATIONS.TWENTY_EIGHT_DAYS,
                        });
                    })
                ),
            ]);

            // Create lookup maps
            const bookMap = new Map<string, ScheduledBook | null>();
            bookIds.forEach((bookId, index) => {
                bookMap.set(bookId, books[index]);
            });

            const venueMap = new Map<string, Venue | null>();
            venueIds.forEach((venueId, index) => {
                venueMap.set(venueId, venues[index]);
            });

            // Combine sessions with resolved static data
            return sessions.map((session) => ({
                ...session,
                book: bookMap.get(session.bookId) ?? null,
                venue: venueMap.get(session.venueId) ?? null,
            }));
        },
        queryKey: sessionsKeys.sessions.month(year, month),
        staleTime: isOld ? DURATIONS.TWENTY_EIGHT_DAYS : DURATIONS.FIVE_MINUTES,
    });

    return query;
}
